import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} Items incremented optimistically on click but not yet confirmed by the server. */
  #optimisticPending = 0;

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.addEventListener(ThemeEvents.cartOptimisticAdd, this.onOptimisticAdd);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.removeEventListener(ThemeEvents.cartOptimisticAdd, this.onOptimisticAdd);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Optimistically increments the cart bubble the instant the add button is clicked,
   * before the network request resolves. The subsequent cart update reconciles the count.
   * @param {CustomEvent<{ quantity?: number }>} event
   */
  onOptimisticAdd = (event) => {
    const quantity = Number(event.detail?.quantity) || 1;
    this.#optimisticPending += quantity;
    this.renderCartBubble(quantity, true);
  };

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';
    const didError = event.detail.data?.didError ?? false;

    // When the add came from a product form, we already incremented optimistically on click.
    // Reconcile here instead of incrementing again (which would double-count).
    if (comingFromProductForm && this.#optimisticPending > 0) {
      const consumed = Math.min(itemCount || this.#optimisticPending, this.#optimisticPending);
      this.#optimisticPending -= consumed;

      if (didError) {
        // The item wasn't actually added — roll back the optimistic increment.
        this.#setCartCount(Math.max(0, this.currentCartCount - consumed));
      }
      // On success the bubble is already correct from the optimistic update.
      return;
    }

    // An authoritative update (cart page, or any non-optimistic source) is the source of truth,
    // so clear any leftover optimistic counter to avoid drift.
    this.#optimisticPending = 0;
    this.renderCartBubble(itemCount, comingFromProductForm, !didError);
  };

  /**
   * Sets the cart bubble to an absolute count without animation, keeping visibility in sync.
   * @param {number} count
   */
  #setCartCount(count) {
    this.refs.cartBubbleCount.classList.toggle('hidden', count === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', count === 0);
    this.currentCartCount = count;
    this.classList.toggle('header-actions__cart-icon--has-cart', count > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(count),
        timestamp: Date.now(),
      })
    );
  }

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
